/** Closed-world delivery diagnostics: never accept customer text or raw errors. */
export const DELIVERY_REASONS = [
  "pending_update", "pending_navigation", "report_unavailable", "recent_launch_only",
  "scope_changed", "dialog_failed", "request_failed", "not_sent", "stale_client",
  "provider_rejected", "outcome_unknown", "reconciliation_failed", "pending_slow",
  "historical_reporting",
] as const;
export type DeliveryReason = typeof DELIVERY_REASONS[number];
export type DeliveryDiagnosticInput = {
  reference: string; reason: DeliveryReason; stage: "interaction" | "dialog" | "request" | "reconciliation";
  brandId?: string; accountId?: string; entityId?: string; level?: string;
  requestedStatus: "ACTIVE" | "PAUSED"; readiness?: string; pending?: boolean;
  durationMs?: number; httpStatus?: number; dispatchState?: "not_sent" | "attempted" | "unknown";
};
export function deliveryDiagnostic(input: DeliveryDiagnosticInput) {
  const uuid = (v: unknown) => typeof v === "string" && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(v) ? v : null;
  const meta = (v: unknown) => typeof v === "string" && /^(act_)?\d{1,32}$/.test(v) ? v.replace(/^act_/, "") : null;
  return {
    event: "ads_manager_delivery_interaction", timestamp: new Date().toISOString(),
    correlationId: uuid(input.reference), correlationIdSource: "browser",
    stage: ["interaction", "dialog", "request", "reconciliation"].includes(input.stage) ? input.stage : null,
    reasonCode: DELIVERY_REASONS.includes(input.reason) ? input.reason : "request_failed",
    // Browser selections are not proof of server authorization.
    selectedScope: { brandId: uuid(input.brandId), accountId: meta(input.accountId) },
    entityId: meta(input.entityId), level: ["campaign", "adset", "ad"].includes(input.level ?? "") ? input.level : null,
    requestedStatus: ["ACTIVE", "PAUSED"].includes(input.requestedStatus) ? input.requestedStatus : null,
    readiness: ["provider_observed", "recent_launch_only", "historical_reporting"].includes(input.readiness ?? "") ? input.readiness : "unknown",
    pending: input.pending === true,
    durationMs: Number.isFinite(input.durationMs) ? Math.max(0, Math.min(input.durationMs!, 3_600_000)) : null,
    httpStatus: Number.isInteger(input.httpStatus) && input.httpStatus! >= 100 && input.httpStatus! <= 599 ? input.httpStatus : null,
    dispatchState: ["not_sent", "attempted"].includes(input.dispatchState ?? "") ? input.dispatchState : "unknown",
  };
}
